'use client';

import { useEffect, useState } from 'react';

import { api } from '@/lib/api';

type CharactersCustomResponse = {
  success?: boolean;
  total?: number;
  totalDocs?: number;
};

const readCharactersTotal = (data: CharactersCustomResponse): number | null => {
  const total = data.total ?? data.totalDocs ?? 0;
  return total > 0 ? total : null;
};

/**
 * Loads published characters count for a selected fandom listing chrome.
 * Uses entity `fandomId=` so the total matches the fandom characters hub
 * (primary fandom), not work-level `fandom=` which also pulls crossover casts.
 * Keeps the previous value while a new request is in flight.
 * When fandomId is absent, returns null without syncing state in the effect.
 */
export const useFandomCharactersCount = (fandomId?: string): number | null => {
  const [charactersCount, setCharactersCount] = useState<number | null>(null);

  useEffect(() => {
    if (!fandomId) {
      return;
    }

    let isCancelled = false;
    void api
      .get<CharactersCustomResponse>('/api/characters-custom', {
        params: {
          fandomId,
          limit: 1,
          minFanfics: 1,
        },
      })
      .then((response) => {
        if (!isCancelled) setCharactersCount(readCharactersTotal(response.data));
      })
      .catch(() => {
        if (!isCancelled) setCharactersCount(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [fandomId]);

  return fandomId ? charactersCount : null;
};
